/**
 * Plattformrollen — Fachprüfer Recht / Fachprüfer FM an Nutzer beliebiger Mandanten vergeben und
 * entziehen (nur Plattform-Root; der Server prüft es).
 *
 * Vergaben werden nie gelöscht: Entzug setzt „entzogen am“, und „Entzogene zeigen“ führt die
 * Historie vor. Wer eine Rolle hat, sieht im Seitenmenü „Regelwerke“.
 */

import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import Section from "../components/Section";
import { useModalContext } from "../components/ModalProvider";
import {
  PLATFORM_ROLES,
  PlatformRole,
  PlatformRoleCandidate,
  PlatformRoleGrant,
  PlatformRoleMailRecipient,
  platformRolesAPI,
} from "../PlatformRolesAPI";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const formatDateTime = (value?: string | null) => (value ? new Date(value).toLocaleString() : "—");

const userLabel = (user: { firstName?: string; lastName?: string; email: string }) =>
  [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;

export default function PlatformRolesPanel() {
  const { t } = useTranslation();
  const { pushError, pushSuccess } = useModalContext() as any;
  const searchId = useId();
  const noteId = useId();

  const [grants, setGrants] = useState<PlatformRoleGrant[]>([]);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState<PlatformRoleCandidate[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  // Rollen vergeben und Nachricht an die Fachprüfer — zwei Aufgaben, zwei Reiter.
  const [tab, setTab] = useState("roles");
  const mailId = useId();
  const [mailRoles, setMailRoles] = useState<PlatformRole[]>(["legalReviewer", "fmReviewer"]);
  const [mailRecipients, setMailRecipients] = useState<PlatformRoleMailRecipient[]>([]);
  const [mailSubject, setMailSubject] = useState("");
  const [mailBody, setMailBody] = useState("Hallo {{firstName}},\n\n");
  const [mailConfirm, setMailConfirm] = useState(false);

  const loadGrants = async () => {
    try {
      const result = await platformRolesAPI.getGrants(includeRevoked);
      setGrants(result?.grants || []);
    } catch (error) {
      pushError(error);
    }
  };

  useEffect(() => {
    loadGrants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeRevoked]);

  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      platformRolesAPI
        .searchUsers(term)
        .then((result) => !cancelled && setCandidates(result || []))
        .catch((error) => !cancelled && pushError(error));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (!mailRoles.length) {
      setMailRecipients([]);
      return;
    }
    let cancelled = false;
    platformRolesAPI
      .mailRecipients(mailRoles)
      .then((recipients) => !cancelled && setMailRecipients(recipients || []))
      .catch((error) => !cancelled && pushError(error));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailRoles.join(","), grants.length]);

  const toggleMailRole = (role: PlatformRole, checked: boolean) =>
    setMailRoles((current) => (checked ? Array.from(new Set(current.concat(role))) : current.filter((r) => r !== role)));

  const mailReady = mailRoles.length > 0 && mailSubject.trim() !== "" && mailBody.trim() !== "";

  const sendMail = async (test: boolean) => {
    setBusy(test ? "mail-test" : "mail");
    try {
      const result = await platformRolesAPI.sendMail(mailRoles, mailSubject.trim(), mailBody.trim(), test);
      if (test) {
        pushSuccess(t("platformRolesPanel.mail.testSent"));
      } else {
        pushSuccess(t("platformRolesPanel.mail.sent", { count: result.sent }));
        if (result.failed?.length) pushError(t("platformRolesPanel.mail.failed", { list: result.failed.join(", ") }));
        setMailConfirm(false);
      }
    } catch (error) {
      pushError(error);
    } finally {
      setBusy(null);
    }
  };

  const holds = (userId: string, role: PlatformRole) =>
    grants.some((grant) => grant.userId === userId && grant.role === role && !grant.revokedAt);

  const grantRole = async (candidate: PlatformRoleCandidate, role: PlatformRole) => {
    setBusy(`${candidate.userId}:${role}`);
    try {
      await platformRolesAPI.grant(candidate.userId, role, note.trim() || undefined);
      pushSuccess(
        t("platformRolesPanel.granted", {
          user: userLabel(candidate),
          role: t(`platformRolesPanel.role.${role}`),
        }),
      );
      setNote("");
      await loadGrants();
    } catch (error) {
      pushError(error);
    } finally {
      setBusy(null);
    }
  };

  const revokeGrant = async (grant: PlatformRoleGrant) => {
    setBusy(grant._id);
    try {
      await platformRolesAPI.revoke(grant._id);
      pushSuccess(t("platformRolesPanel.revoked", { user: grant.email, role: t(`platformRolesPanel.role.${grant.role}`) }));
      setConfirmRevoke(null);
      await loadGrants();
    } catch (error) {
      pushError(error);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex w-full min-w-0 max-w-[1200px] flex-col gap-4">
      <h2 className="min-w-0 break-words text-lg font-semibold">{t("platformRolesPanel.title")}</h2>
      <p className="min-w-0 break-words text-sm text-muted-foreground">{t("platformRolesPanel.subtitle")}</p>

      <Tabs value={tab} onValueChange={setTab} className="min-w-0">
        <TabsList>
          <TabsTrigger value="roles">{t("platformRolesPanel.tabs.roles")}</TabsTrigger>
          <TabsTrigger value="mail">{t("platformRolesPanel.tabs.mail")}</TabsTrigger>
        </TabsList>
        <TabsContent value="roles" className="flex min-w-0 flex-col gap-4">
          <Section title={t("platformRolesPanel.assignTitle")}>
            <div className="flex min-w-0 flex-col gap-3">
              <div className="flex min-w-0 flex-wrap gap-3">
                <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:min-w-[260px]">
                  <Label htmlFor={searchId}>{t("platformRolesPanel.searchLabel")}</Label>
                  <Input
                    id={searchId}
                    value={search}
                    placeholder={t("platformRolesPanel.searchPlaceholder")}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:min-w-[260px]">
                  <Label htmlFor={noteId}>{t("platformRolesPanel.noteLabel")}</Label>
                  <Input
                    id={noteId}
                    value={note}
                    placeholder={t("platformRolesPanel.notePlaceholder")}
                    onChange={(event) => setNote(event.target.value)}
                  />
                </div>
              </div>

              {candidates.length > 0 ? (
                <div className="min-w-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("platformRolesPanel.user")}</TableHead>
                        <TableHead>{t("platformRolesPanel.tenant")}</TableHead>
                        <TableHead>{t("platformRolesPanel.assign")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {candidates.map((candidate) => (
                        <TableRow key={candidate.userId}>
                          <TableCell className="min-w-0 break-words">
                            <div className="font-medium">{userLabel(candidate)}</div>
                            <div className="text-xs text-muted-foreground">{candidate.email}</div>
                          </TableCell>
                          <TableCell className="min-w-0 break-words">
                            {candidate.customerName || candidate.customer}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-2">
                              {PLATFORM_ROLES.map((role) => (
                                <Button
                                  key={role}
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  disabled={holds(candidate.userId, role) || busy !== null}
                                  onClick={() => grantRole(candidate, role)}
                                >
                                  {busy === `${candidate.userId}:${role}` ? (
                                    <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                                  ) : null}
                                  {holds(candidate.userId, role)
                                    ? t("platformRolesPanel.holds", { role: t(`platformRolesPanel.role.${role}`) })
                                    : t(`platformRolesPanel.role.${role}`)}
                                </Button>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : search.trim().length >= 2 ? (
                <p className="text-sm text-muted-foreground">{t("platformRolesPanel.noUsers")}</p>
              ) : null}
            </div>
          </Section>

          <Section title={t("platformRolesPanel.grantsTitle")}>
            <div className="mb-3 flex items-center gap-2">
              <Checkbox
                id={`${searchId}-revoked`}
                checked={includeRevoked}
                onCheckedChange={(checked) => setIncludeRevoked(checked === true)}
              />
              <Label htmlFor={`${searchId}-revoked`}>{t("platformRolesPanel.includeRevoked")}</Label>
            </div>
            {grants.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("platformRolesPanel.noGrants")}</p>
            ) : (
              <div className="min-w-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("platformRolesPanel.user")}</TableHead>
                      <TableHead>{t("platformRolesPanel.tenant")}</TableHead>
                      <TableHead>{t("platformRolesPanel.roleColumn")}</TableHead>
                      <TableHead>{t("platformRolesPanel.grantedColumn")}</TableHead>
                      <TableHead>{t("platformRolesPanel.statusColumn")}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {grants.map((grant) => (
                      <TableRow key={grant._id}>
                        <TableCell className="min-w-0 break-words">{grant.email}</TableCell>
                        <TableCell className="min-w-0 break-words">{grant.customerName || grant.customer || "—"}</TableCell>
                        <TableCell>{t(`platformRolesPanel.role.${grant.role}`)}</TableCell>
                        <TableCell className="min-w-0 break-words text-xs">
                          <div>{t("platformRolesPanel.grantedBy", { by: grant.grantedBy, date: formatDateTime(grant.grantedAt) })}</div>
                          {grant.note ? <div className="text-muted-foreground">{grant.note}</div> : null}
                        </TableCell>
                        <TableCell className="min-w-0 break-words text-xs">
                          {grant.revokedAt
                            ? t("platformRolesPanel.revokedBy", { by: grant.revokedBy, date: formatDateTime(grant.revokedAt) })
                            : t("platformRolesPanel.active")}
                        </TableCell>
                        <TableCell>
                          {grant.revokedAt ? null : confirmRevoke === grant._id ? (
                            <div className="flex flex-wrap gap-2">
                              <Button size="sm" variant="destructive" disabled={busy !== null} onClick={() => revokeGrant(grant)}>
                                {t("platformRolesPanel.revokeConfirm")}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setConfirmRevoke(null)}>
                                {t("global.cancel")}
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => setConfirmRevoke(grant._id)}>
                              {t("platformRolesPanel.revoke")}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Section>
        </TabsContent>
        <TabsContent value="mail" className="flex min-w-0 flex-col gap-4">
          <Section title={t("platformRolesPanel.mail.title")}>
            <div className="flex min-w-0 flex-col gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-2">
                <span className="text-sm font-medium">{t("platformRolesPanel.mail.to")}</span>
                {PLATFORM_ROLES.map((role) => (
                  <div key={role} className="flex items-center gap-2">
                    <Checkbox
                      id={`${mailId}-${role}`}
                      checked={mailRoles.includes(role)}
                      onCheckedChange={(checked) => toggleMailRole(role, checked === true)}
                    />
                    <Label htmlFor={`${mailId}-${role}`}>{t(`platformRolesPanel.role.${role}`)}</Label>
                  </div>
                ))}
                <span className="text-sm text-muted-foreground">
                  {t("platformRolesPanel.mail.recipients", { count: mailRecipients.length })}
                </span>
              </div>
              {mailRecipients.length ? (
                <p className="m-0 min-w-0 break-words text-xs text-muted-foreground">
                  {mailRecipients.map((recipient) => recipient.email).join(", ")}
                </p>
              ) : null}
              <div className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor={`${mailId}-subject`}>{t("platformRolesPanel.mail.subject")}</Label>
                <Input id={`${mailId}-subject`} value={mailSubject} onChange={(event) => setMailSubject(event.target.value)} />
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor={`${mailId}-body`}>{t("platformRolesPanel.mail.body")}</Label>
                <Textarea id={`${mailId}-body`} rows={8} value={mailBody} onChange={(event) => setMailBody(event.target.value)} />
                <span className="text-xs text-muted-foreground">{t("platformRolesPanel.mail.bodyHint")}</span>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <Button variant="outline" disabled={!mailReady || busy !== null} onClick={() => sendMail(true)}>
                  {busy === "mail-test" ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
                  {t("platformRolesPanel.mail.test")}
                </Button>
                {mailConfirm ? (
                  <>
                    <Button variant="destructive" disabled={!mailReady || busy !== null} onClick={() => sendMail(false)}>
                      {busy === "mail" ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
                      {t("platformRolesPanel.mail.confirm", { count: mailRecipients.length })}
                    </Button>
                    <Button variant="outline" onClick={() => setMailConfirm(false)}>
                      {t("global.cancel")}
                    </Button>
                  </>
                ) : (
                  <Button disabled={!mailReady || !mailRecipients.length || busy !== null} onClick={() => setMailConfirm(true)}>
                    {t("platformRolesPanel.mail.send", { count: mailRecipients.length })}
                  </Button>
                )}
              </div>
            </div>
          </Section>

        </TabsContent>
      </Tabs>
    </div>
  );
}
